<template>
  <div class="task-detail-table">
    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="filterKeyword"
        placeholder="设备编号/名称"
        clearable
        style="width: 200px"
        @keyup.enter.native="handleSearch"
      />
      <AreaCascader
        v-model="filterAreaId"
        :options="currentSubAreas"
        placeholder="所属区域"
        clearable
        style="width: 180px"
        @change="handleSearch"
      />
      <el-select
        v-model="filterStatus"
        placeholder="检查结果"
        clearable
        style="width: 130px"
        @change="handleSearch"
      >
        <el-option label="未执行" :value="0"/>
        <el-option label="已完成" :value="1"/>
        <el-option label="异常项" :value="2"/>
        <el-option label="故障忽略" :value="3"/>
      </el-select>
      <el-select
        v-model="filterExecuteMode"
        placeholder="执行方式"
        clearable
        style="width: 130px"
        @change="handleSearch"
      >
        <el-option label="手动确认" value="manual"/>
        <el-option label="NFC打卡" value="nfc"/>
        <el-option label="批量确认" value="batch"/>
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
      <el-button icon="el-icon-refresh" @click="handleResetFilter">重置</el-button>
    </div>

    <!-- 表格数据 -->
    <el-table v-loading="loading" :data="detailList" size="mini" border>
      <el-table-column prop="deviceCode" label="设备编号" min-width="80" align="center"/>
      <el-table-column prop="deviceName" label="设备名称" min-width="100" align="center"/>
      <el-table-column prop="areaPath" label="所属区域" min-width="200"/>
      <el-table-column label="检查结果" width="150" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.status === 0" type="info" size="mini">未执行</el-tag>
          <el-tag v-else-if="scope.row.status === 3" type="info" size="mini">故障忽略</el-tag>
          <template v-else>
            <el-tag :type="parseResultTag(scope.row)" size="mini">{{ parseResultSummary(scope.row) }}</el-tag>
            <el-tag
              v-if="scope.row.status === 2 && scope.row.faultId"
              type="warning"
              effect="plain"
              size="mini"
              style="margin-left:4px"
            >已建单</el-tag>
            <el-button
              v-if="hasAbnormal(scope.row.executeResult)"
              type="text"
              size="mini"
              style="margin-left:4px"
              @click.stop="showResultDetail(scope.row)"
            >
              查看
            </el-button>
          </template>
        </template>
      </el-table-column>
      <el-table-column label="执行方式" width="80" align="center">
        <template slot-scope="scope">
          <span>{{ formatExecuteMode(scope.row.executeMode) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="执行人" width="90" align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.executeUserName || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="执行时间" width="130" align="center">
        <template slot-scope="scope">
          <span>{{ formatDateTime(scope.row.executeTime) || '-' }}</span>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      small
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-sizes="[10, 20, 50]"
      :page-size="pageSize"
      :current-page="pageNo"
      style="text-align: right; margin-top: 10px;"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    />

    <!-- 检查项详情弹窗 -->
    <el-dialog
      title="检查项详情"
      :visible.sync="resultDetailVisible"
      width="900px"
      append-to-body
    >
      <el-alert
        v-if="resultDetailSourceRow && resultDetailSourceRow.faultId"
        :title="faultTicketBannerText"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 12px"
      />
      <el-table :data="currentResultItems" size="mini" border>
        <el-table-column prop="displayContent" label="检查内容" min-width="120"/>
        <el-table-column label="结果" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.result === 'fault' ? 'danger' : 'success'" size="mini">
              {{ scope.row.result === 'fault' ? '异常' : '正常' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="extraContent" label="额外内容" min-width="150" show-overflow-tooltip/>
        <el-table-column prop="faultDesc" label="异常描述" min-width="150" show-overflow-tooltip/>
        <el-table-column label="现场照片" min-width="150">
          <template slot-scope="scope">
            <span v-if="!scope.row.photos || !scope.row.photos.length" style="color:#c0c4cc">-</span>
            <span v-else-if="scope.row.photosLoading" style="color:#909399; font-size:12px">图片加载中…</span>
            <div v-else class="photo-thumb-list">
              <el-image
                v-for="(url, pIdx) in scope.row.photoUrls"
                :key="pIdx"
                :src="url"
                :preview-src-list="scope.row.photoUrls"
                class="photo-thumb"
                fit="cover"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button @click="resultDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 服务
import { taskService } from './task.service';
import { areaService } from '@/features/gnmk/area/area.service';
// 工具函数
import { findAreaNodeByUuid } from './utils/areaHandler.js';
import { createObjectUrlFromBlobData } from '@/features/common/utils/downloadFileUtil.js';
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'TaskDetailTable',
  components: { AreaCascader },
  props: {
    taskId: {
      type: String,
      required: true
    },
    zoneAreaId: {
      type: String,
      default: null
    },
    /** 完整区域树（列表页传入）；为空时兜底请求接口 */
    areaTree: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      fallbackAreaTree: [],
      filterKeyword: '',
      /** null 表示未选；数字 0～3 与后端 status 一致 */
      filterStatus: null,
      /** 与设备台账一致：仅传当前选中区域 uuid（后端 areaId 精确匹配） */
      filterAreaId: null,
      filterExecuteMode: '',
      detailList: [],
      loading: false,
      pageNo: 1,
      pageSize: 10,
      total: 0,
      // 检查项详情弹窗
      resultDetailVisible: false,
      // 每项附带 photoUrls（已解析的objectURL数组）和 photosLoading 字段
      currentResultItems: [],
      /** 打开检查项详情时对应的表格行，用于展示关联故障单提示 */
      resultDetailSourceRow: null,
      // 本次弹窗产生的 objectURL，关闭时统一释放
      photoObjectUrls: []
    };
  },
  computed: {
    /** 与 task-group-dialog 的 currentSubAreas 一致：大区下仅此节点为根的子树供筛选 */
    resolvedAreaTree () {
      const t = this.areaTree && this.areaTree.length ? this.areaTree : this.fallbackAreaTree;
      return t || [];
    },
    currentSubAreas () {
      if (!this.zoneAreaId || !this.resolvedAreaTree.length) return this.resolvedAreaTree;
      const zone = findAreaNodeByUuid(this.resolvedAreaTree, this.zoneAreaId);
      return zone ? [zone] : [];
    },
    faultTicketBannerText () {
      const row = this.resultDetailSourceRow;
      if (!row || !row.faultId) return '';
      const code = row.faultCode ? String(row.faultCode) : '-';
      return `已记录故障单，单号：${code}`;
    }
  },
  mounted () {
    if (!this.areaTree || !this.areaTree.length) {
      areaService.getAreaTree().then(res => {
        this.fallbackAreaTree = res.data || [];
      });
    }
  },
  methods: {
    formatDateTime,

    buildQueryParams () {
      const params = {
        taskId: this.taskId,
        pageNo: this.pageNo,
        pageSize: this.pageSize
      };
      const kw = (this.filterKeyword || '').trim();
      if (kw) params.keyword = kw;
      if (this.filterStatus !== null && this.filterStatus !== '') {
        params.status = this.filterStatus;
      }
      if (this.filterAreaId != null && this.filterAreaId !== '') {
        params.areaId = this.filterAreaId;
      }
      const em = (this.filterExecuteMode || '').trim();
      if (em) params.executeMode = em;
      return params;
    },

    handleSearch () {
      this.pageNo = 1;
      this.loadData();
    },

    handleResetFilter () {
      this.filterKeyword = '';
      this.filterStatus = null;
      this.filterAreaId = null;
      this.filterExecuteMode = '';
      this.pageNo = 1;
      this.loadData();
    },

    loadData () {
      this.loading = true;
      taskService.queryTaskDetailPage(this.buildQueryParams()).then(res => {
        const data = res.data;
        this.detailList = data.list || data.rows || [];
        this.total = data.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    handleSizeChange (val) {
      this.pageSize = val;
      this.pageNo = 1;
      this.loadData();
    },
    handlePageChange (val) {
      this.pageNo = val;
      this.loadData();
    },
    formatExecuteMode (mode) {
      const map = { manual: '手动确认', nfc: 'NFC打卡', batch: '批量确认' };
      return map[mode] || '-';
    },
    parseExecuteResult (json) {
      if (!json) return [];
      try {
        const items = JSON.parse(json);
        return Array.isArray(items) ? items : [];
      } catch {
        return [];
      }
    },
    /** 管理端明细需要把检查对象带入内容名称，便于区分同一设备下的同名检查项。 */
    buildDisplayContent (item, row) {
      const content = item && item.content != null ? String(item.content).trim() : '';
      const contentMap = ((row && row.checkContentList) || []).reduce((map, checkContent) => {
        if (checkContent && checkContent.uuid) map[checkContent.uuid] = checkContent;
        return map;
      }, {});
      const checkContent = item && item.contentId ? contentMap[item.contentId] : null;
      const checkObjectName = checkContent && checkContent.checkObjectName
        ? String(checkContent.checkObjectName).trim()
        : (item && item.checkObjectName ? String(item.checkObjectName).trim() : '');
      const actualContent = checkContent && checkContent.content != null
        ? String(checkContent.content).trim()
        : content;
      return checkObjectName && actualContent
        ? `${checkObjectName}-${actualContent}`
        : (actualContent || '-');
    },
    /** 兼容旧数据：没有 result 字段的记录按异常项处理 */
    isFaultResultItem (item) {
      return !item || !item.result || item.result === 'fault';
    },
    parseResultSummary (row) {
      const { executeResult, status } = row;
      if (!executeResult) {
        return status === 1 ? '全部正常' : '-';
      }
      const items = this.parseExecuteResult(executeResult);
      const faultCount = items.filter(item => this.isFaultResultItem(item)).length;
      const extraCount = items.filter(item => !this.isFaultResultItem(item) && item.extraContent).length;
      if (faultCount > 0) return `${faultCount}项异常`;
      return extraCount > 0 ? `全部正常（${extraCount}项有记录）` : '全部正常';
    },
    parseResultTag (row) {
      const { executeResult, status } = row;
      if (!executeResult) {
        return status === 1 ? 'success' : 'info';
      }
      return this.parseExecuteResult(executeResult).some(item => this.isFaultResultItem(item)) ? 'danger' : 'success';
    },
    /** 存在异常或额外内容时均允许查看结果详情 */
    hasAbnormal (json) {
      return this.parseExecuteResult(json).length > 0;
    },
    /** 点击「查看」打开检查详情弹窗，先展示文字内容，照片后台异步加载 */
    showResultDetail (row) {
      // 释放上次缓存的 objectURL
      this.photoObjectUrls.forEach(url => URL.revokeObjectURL(url));
      this.photoObjectUrls = [];

      this.resultDetailSourceRow = row && row.faultId ? row : null;

      const items = this.parseExecuteResult(row.executeResult);

      this.currentResultItems = items.map(item => ({
        ...item,
        displayContent: this.buildDisplayContent(item, row),
        result: this.isFaultResultItem(item) ? 'fault' : 'normal',
        photoUrls: [],
        photosLoading: !!(item.photos && item.photos.length)
      }));
      this.resultDetailVisible = true;

      // 后台逐项异步加载照片，互不阻塞
      items.forEach((item, idx) => {
        if (!item.photos || !item.photos.length) return;
        Promise.all(
          item.photos.map(fileId => this.downloadPhotoAsObjectUrl(fileId).catch(() => null))
        ).then(urls => {
          const photoUrls = urls.filter(Boolean);
          // 记录 objectURL 以便弹窗关闭时释放
          this.photoObjectUrls.push(...photoUrls);
          this.$set(this.currentResultItems, idx, {
            ...this.currentResultItems[idx],
            photoUrls,
            photosLoading: false
          });
        }).catch(() => {
          this.$set(this.currentResultItems, idx, {
            ...this.currentResultItems[idx],
            photosLoading: false
          });
        });
      });
    },

    /**
     * 通过 initDownload → download (blob) 两步流程拉取图片，返回 objectURL（用于 el-image 预览）。
     * objectURL 需在使用完毕后调用 URL.revokeObjectURL 释放。
     */
    async downloadPhotoAsObjectUrl (fileId) {
      const initRes = await this.$axios.get(`${this.$config.fileApi.initDownload}/${fileId}`);
      const filePath = initRes.data.data;
      const dlRes = await this.$axios.get(`${this.$config.fileApi.download}/${filePath}`, { responseType: 'blob' });
      return createObjectUrlFromBlobData(dlRes.data);
    }
  },
  watch: {
    // immediate:true 确保初次挂载时也触发加载，解决首次打开不请求数据的问题
    taskId: {
      immediate: true,
      handler (newVal) {
        this.pageNo = 1;
        this.loadData();
      }
    },
    // 弹窗关闭时释放所有 objectURL，防止内存泄漏
    resultDetailVisible (val) {
      if (!val) {
        this.photoObjectUrls.forEach(url => URL.revokeObjectURL(url));
        this.photoObjectUrls = [];
        this.resultDetailSourceRow = null;
      }
    }
  }
};
</script>

<style scoped>
.task-detail-table {
  padding: 0px 0px 10px 0px;
  background: #f9fafc;
}

/* 与设备台账搜索栏一致，避免弹窗内样式突兀 */
.search-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
}

.photo-thumb-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
}

.photo-thumb {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  cursor: pointer;
}
</style>

<!-- 全局覆盖：el-image 图片预览浮层在嵌套弹窗中 z-index 不足，此处强制提升至弹窗层级之上 -->
<style>
.el-image-viewer__wrapper { z-index: 3000 !important; }
</style>
